import { useMutation, type UseMutationOptions } from '@tanstack/react-query';
import type { IssueSeverity, IssueStatus } from '@chatsdet/types/issues';
import { API_URL } from '../config';
import { toApiError } from '../lib/api-error';

type BulkReproVerifyFilters = {
  severity?: IssueSeverity;
  propertyId?: string;
  status?: IssueStatus;
  search?: string;
  lastSeenStart?: string;
  lastSeenEnd?: string;
  categoryIds?: string[];
  includeArchived?: boolean;
};

type BulkReproVerifyPayload =
  | {
      issueIds: string[];
    }
  | {
      selectAll: true;
      filters: BulkReproVerifyFilters;
      excludeIssueIds?: string[];
    };

type BulkReproVerifyResult = {
  enqueuedCount: number;
  skippedCount: number;
};

export function useBulkReproVerify(
  options?: UseMutationOptions<BulkReproVerifyResult, Error, BulkReproVerifyPayload>
) {
  return useMutation<BulkReproVerifyResult, Error, BulkReproVerifyPayload>({
    mutationFn: async (payload) => {
      const response = await fetch(`${API_URL}/v2/issues/bulk-repro-verify`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'content-type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        throw await toApiError(response, 'Failed to queue issues for verification');
      }

      const parsed = await response.json();

      if (!parsed?.ok) {
        throw await toApiError(response, 'Failed to queue issues for verification');
      }

      return {
        enqueuedCount: Number(parsed.enqueuedCount ?? 0),
        skippedCount: Number(parsed.skippedCount ?? 0),
      };
    },
    ...options,
  });
}
